<template>	
	
	<el-row :style="rowStyle">
		
		<el-col :span="descriptionWidth" style="padding: 20px; overflow-y: auto; height: 100%;">
			
			<!-- Title / Meta -->
			
			<legend class="ticket__legend">{{ ticket.name }}</legend>
			
			<div class="tag-container">
				<el-tag :type="statusType" :title="statusDescription">
					<ion-icon :name="statusIcon"></ion-icon>{{ ticket.status }}
				</el-tag>
				<el-tag v-if="ticket.due_date" :class="{ 'ticket--overdue': taskOverdue }" type="info" title="Due date">
					<ion-icon name="calendar"></ion-icon>{{ ticket.due_date | dateFormatted }}
				</el-tag>
				<el-tag type="info" title="Category">
					<ion-icon name="checkbox"></ion-icon>{{ ticket.type }}
				</el-tag>
			</div>
			
			<div v-if="showAssigned && ticket.assigned.length" class="tag-container">
				<UserTag v-for="(user, i) in ticket.assigned" :user="user" :key="i"/>
			</div>
			
			<!-- Description -->
			
			<TextEditor v-if="hasDescription" :editable="false" :content="ticket.description" style="margin-top: 16px;"/>
			
			<hr class="ticket__divider">
			
			<!-- Checklists -->
			
			<legend class="ticket__legend">Checklists<el-button size="small" type="text" style="float: right; padding: 5px 10px;" icon="el-icon-plus" round @click="onClickAddListButton">Add List</el-button></legend>
			
			<draggable v-model="ticket.checklists" handle=".checklist__handle" group="checklists" ghost-class="checklist__ghost" @end="onUpdateChecklistOrder">
				<Checklist v-for="(checklist, index) in ticket.checklists" :checklist="checklist" :index="index" :key="index" @event="onChecklistEvent"/>
			</draggable>
			
			<p v-if="ticket.checklists.length === 0" class="empty-state">There are no checklists.</p>
			
		</el-col>
		
		<el-col v-if="showComments" :span="commentsWidth" width="40%" style="padding: 20px; overflow-y: auto; height: 100%;">
			
			<legend class="ticket__legend">Internal Comments</legend>
			
			<CommentBox style="margin-bottom: 20px;" @on-add="onAddComment"/>
			
			<div class="comments-list">
				
				<Comment v-for="comment in ticket.comments" :key="comment.id" :comment="comment" :ticket_id="ticket.id" @on-delete="onDeleteComment"/>
				
				<p v-if="ticket.comments.length === 0" class="empty-state">There are no comments.</p>
				
			</div>
			
		</el-col>
		
	</el-row>
	
</template>

<script>
	
	const randomstring = require("randomstring");
	
	import TextEditor from "@/components/TextEditor";
	
	import Comment from "@/components/Comments/Comment";
	import CommentBox from "@/components/Comments/CommentBox";
	
	import UserTag from "@/components/Users/Tag";
	
	import Checklist from "@/components/Checklist";
	
	import Draggable from "vuedraggable";
	
	export default {
		
		components: { TextEditor, Comment, CommentBox, UserTag, Checklist, Draggable },
		
		props: {
			ticket: { default() { return {}; }, type: Object },
			layout: { default: "horizontal", type: String },
			showComments: { default: true, type: Boolean },
			showAssigned: { default: false, type: Boolean },
		},
		
		computed: {
			
			descriptionWidth() {
				return this.layout === "horizontal" ? 14 : 24;
			},
			
			commentsWidth() {
				return this.layout === "horizontal" ? 10 : 24;
			},
			
			rowStyle() {
				if (this.layout === "horizontal") {
					return {
						"height": "100%",
						"overflow": "hidden",
					};
				}
			},
			
			columnStyle() {
				
			},
			
			hasDescription() {
				return !this.$options.filters.isDescriptionBlank(this.ticket.description);
			},
			
			statusType() {
				switch (this.ticket.status) {
					case "Rejected":
						return "danger";
					case "Approved":
						return "warning";
					case "In Progress":
						return "warning";
					case "Completed":
						return "success";
					case "Finalised":
						return "success";
					default:
						return "";
				}
			},
			
			statusIcon() {
				switch (this.ticket.status) {
					case "Rejected":
						return "close";
					case "Approved":
						return "checkmark";
					case "In Progress":
						return "sync";
					case "Completed":
						return "done-all";
					case "Finalised":
						return "sunny";
					default:
						return "time";
				}
			},
			
			statusDescription() {
				switch (this.ticket.status) {
					case "Rejected":
						return "This task has been rejected";
					case "Approved":
						return "This task has been approved";
					case "In Progress":
						return "This task is in progress";
					case "Completed":
						return "This task has been completed";
					case "Finalised":
						return "This task has been finalised";
					default:
						return "This task is pending";
				}
			},
			
			taskOverdue() {
				return new Date() > new Date(this.ticket.due_date);
			},
			
		},
		
		methods: {
			
			reorderChecklists() {
				// Update remote checklist order.
				this.axios.post("checklists-order", { checklists: this.ticket.checklists });
			},
			
			onChecklistEvent(e) {
				if (e.name === "add-list") {
					// New checklist.
					const checklist = { public_id: randomstring.generate(5), ticket_id: this.ticket.id, title: "Checklist", items: [] };
					// Update local list.
					this.ticket.checklists.unshift(checklist);
					// Update database.
					this.axios.post("checklists", checklist).then((r) => {
						if (r.status === 201) {
							this.$set(checklist, "id", r.data.id);
						}
						this.reorderChecklists();
					});
				} else if (e.name === "update-list") {
					// Update database.
					this.axios.post(`checklists/${e.list.public_id}`, e.list).then(this.reorderChecklists);
				} else if (e.name === "duplicate-list") {
					const original = this.ticket.checklists[e.index];
					const duplicated = { ... JSON.parse(JSON.stringify(original)), id: null, public_id: randomstring.generate(5) };
					// Update local list.
					this.ticket.checklists.push(duplicated);
					// Update database.
					this.axios.post("checklists", duplicated).then((r) => {
						if (r.status === 201) {
							this.$set(duplicated, "id", r.data.id);
						}
						this.reorderChecklists();
					});
				} else if (e.name === "delete-list") {
					const index = e.index;
					const list = this.ticket.checklists[index];
					if (list.items.length) {
						this.$confirm("Are you sure you want to delete this checklist?", "Confirmation", {
							confirmButtonText: "Delete",
							confirmButtonClass: "el-button--danger",
							cancelButtonText: "Cancel",
						}).then(() => {
							// Update local list.
							this.ticket.checklists.splice(index, 1);
							// Update database.
							this.axios.delete(`checklists/${list.public_id}`).then(this.reorderChecklists);
						});
					} else {
						// Update local list.
						this.ticket.checklists.splice(index, 1);
						// Update database.
						this.axios.delete(`checklists/${list.public_id}`).then(this.reorderChecklists);
					}
				} else if (e.name === "add-item") {
					this.axios.post("checklist-items", e.item).then(this.reorderChecklists);
				} else if (e.name === "update-item") {
					this.axios.post(`checklist-items/${e.item.public_id}`, e.item).then();
				} else if (e.name === "delete-item") {
					this.axios.delete(`checklist-items/${e.item.public_id}`).then(this.reorderChecklists);
				} else if (e.name === "reorder-lists" || e.name === "reorder-items") {
					this.reorderChecklists();
				}
			},
			
			onUpdateChecklistOrder(e) {
				this.onChecklistEvent({ name: "reorder-lists" });
			},
			
			onClickAddListButton() {
				this.onChecklistEvent({ name: "add-list" });
			},
			
			onDeleteComment: function(e) {
				if (e.success) {
					this.$store.dispatch("refreshSelectedTicket", {
						id: this.$route.params.ticketId,
						callback: () => {
							
						}
					});
				}
			},
			
			onAddComment(text) {
				this.axios.post("comments", {
					project_id: this.ticket.project_id,
					ticket_id: this.ticket.id,
					user_id: this.$auth.user().id,
					text: text,
				}).then((r) => {
					this.$store.dispatch("refreshSelectedTicket", {
						id: this.$route.params.ticketId,
						callback: () => {
							this.$notify.success({
								title: "Success!",
								message: "Your comment has been posted.",
							});
						}
					});
				}).catch(function(e) {
					this.$notify.error({
						title: "Uh oh!",
						message: "An unknown error occurred while posting your comment.",
					});
				});
			},
			
			onClickTab(tab, i) {
				
			}
			
		},
		
		/*watch: {
			
			"ticket.checklists": {
				deep: true,
				handler(value1, value2) {
					this.updateChecklists();
				},
			}
			
		},
		
		mounted() {
			
			this.updateChecklists = _.debounce(() => {
				this.axios.post("checklists", {
					ticket_id: this.ticket.id,
					checklists: this.ticket.checklists,
				}).then((r) => {
					
				}).catch(function(e) {
					
				});
			}, 1000);
			
		},*/
		
	}
	
</script>

<style scoped>
	
	
	
</style>


// WEBPACK FOOTER //
// src/components/Projects/Project/TicketDetails.vue